// Meal sharing — send a logged meal to a connected partner, view inbox,
// accept (forks into recipient's own meal) or dismiss.

import { supabase } from "./supabase";
import { saveMealRemote } from "./storage";
import type { MealEstimate, MealType, LoggedMeal } from "./types";

export interface SharedMealInboxItem {
  shareId: string;
  sharerId: string;
  sharerEmail: string;
  note: string | null;
  createdAt: string;
  /** Full meal payload (joined via RLS — see "meals shared with me" policy). */
  meal: LoggedMeal;
}

/** Share an existing meal with one or more connected partners. */
export async function shareMeal(
  sourceMealId: string,
  recipientIds: string[],
  note?: string
): Promise<{ created: number; skipped: number }> {
  if (!supabase) throw new Error("Supabase not configured");
  if (recipientIds.length === 0) return { created: 0, skipped: 0 };

  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  // The unique (source_meal_id, recipient_id) constraint means a second share
  // of the same meal to the same recipient is a no-op — we use ON CONFLICT
  // semantics via Prefer header.
  const rows = recipientIds.map((rid) => ({
    source_meal_id: sourceMealId,
    sharer_id: user.id,
    recipient_id: rid,
    note: note?.trim() || null
  }));
  const { data, error } = await supabase
    .from("meal_shares")
    .upsert(rows, { onConflict: "source_meal_id,recipient_id", ignoreDuplicates: true })
    .select("id");
  if (error) throw error;
  return { created: data?.length ?? 0, skipped: rows.length - (data?.length ?? 0) };
}

/** Load pending shared meals — items the user hasn't accepted or dismissed yet. */
export async function loadInbox(): Promise<SharedMealInboxItem[]> {
  if (!supabase) return [];
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return [];

  // We have to join meals via the inline select because PostgREST embedded
  // resources respect RLS — the "meals shared with me" policy lets us read
  // exactly the source meals we have active shares for.
  const { data, error } = await supabase
    .from("meal_shares")
    .select(
      `id, sharer_id, note, created_at,
       meal:meals!source_meal_id(*)`
    )
    .eq("recipient_id", user.id)
    .is("accepted_at", null)
    .is("dismissed_at", null)
    .order("created_at", { ascending: false });
  if (error) throw error;

  // Sharer emails — same partner_emails() RPC we use elsewhere.
  const { data: emails } = await supabase.rpc("partner_emails");
  const emailMap = new Map<string, string>();
  for (const row of (emails ?? []) as Array<{ user_id: string; email: string }>) {
    if (row.user_id && row.email) emailMap.set(row.user_id, row.email);
  }

  return (data ?? [])
    .filter((r) => r.meal) // Defensive: skip if RLS hid the source meal
    .map((r) => ({
      shareId: r.id,
      sharerId: r.sharer_id,
      sharerEmail: emailMap.get(r.sharer_id) ?? `user ${r.sharer_id.slice(0, 8)}`,
      note: r.note,
      createdAt: r.created_at,
      meal: r.meal as unknown as LoggedMeal
    }));
}

/** Count unread inbox items — used for the nav badge. */
export async function inboxCount(): Promise<number> {
  if (!supabase) return 0;
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return 0;
  const { count } = await supabase
    .from("meal_shares")
    .select("id", { count: "exact", head: true })
    .eq("recipient_id", user.id)
    .is("accepted_at", null)
    .is("dismissed_at", null);
  return count ?? 0;
}

/**
 * Accept a shared meal — saves the (possibly adjusted) estimate as a NEW
 * meal in the recipient's account, then marks the share accepted.
 *
 * If `stripNutrients` is true (Free recipients), the 25-nutrient panel is
 * dropped from each item before save — Free users get calories + macros,
 * Pro recipients keep the full data. This is the meal-sync paywall: pushes
 * Free users to upgrade if they want the rich nutrient data their Pro
 * partners are sharing.
 */
export async function acceptShare(
  shareId: string,
  estimate: MealEstimate,
  mealType: MealType,
  options: { stripNutrients?: boolean } = {}
): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");

  // For Free recipients, downgrade the estimate before save.
  const finalEstimate = options.stripNutrients
    ? {
        ...estimate,
        items: estimate.items.map((it) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { nutrients: _drop, ...rest } = it;
          return rest;
        })
      }
    : estimate;

  // Save as a new meal in recipient's account (uses existing saveMealRemote).
  // No caption / photo carries over — this is the recipient's own copy.
  const saved = await saveMealRemote({
    meal_type: mealType,
    estimate: finalEstimate,
    source_providers: ["shared"],
    disagreement_pct: 0
  });

  // Mark the share accepted, link the new meal id
  const { error } = await supabase
    .from("meal_shares")
    .update({
      accepted_at: new Date().toISOString(),
      recipient_meal_id: saved.id
    })
    .eq("id", shareId);
  if (error) throw error;
}

/** Dismiss a share without logging it. */
export async function dismissShare(shareId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("meal_shares")
    .update({ dismissed_at: new Date().toISOString() })
    .eq("id", shareId);
  if (error) throw error;
}
